
import { useState } from "react";
import { AppSupabaseClient, MembershipRoleRow, MembershipRow, OrganizationRow, SharingMinistryType, SmiPlan } from "../../../types/supabase";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxToolkit";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { useAsyncEffect } from "../../hooks/useAsyncEffect";
import { useClient } from "../../providers/supabase";
import { useBackendApi } from "../../hooks/useBackendApi";
import { selectMembershipForUser } from "../../../lib/queries/selectMembershipForUser";
import { onSyncMembership, onUpdateMemberNumber } from "../../reduxToolkit/membershipSlice";
import { RequireAuthProvided } from "../../wrappers/requireAuth";
import { saveMyMembership, updateMyMembership } from "@healthshare/shared/commands/memberships";
import { Link } from "react-router";
import { selectOrganizationForUser } from "../../../lib/queries/selectOrganizationForUser";

export interface OnboardingSetMembershipDetailsProps extends RequireAuthProvided {
  onComplete: () => void
}

export function OnboardingSetMembershipDetails({user, onComplete}: OnboardingSetMembershipDetailsProps) {
  const membershipSlice = useAppSelector((s) => s.membership)
  const dispatch = useAppDispatch()
  const client = useClient()

  const init = useAsyncEffect(async () => {
    // user.id may be transiently undefined -- e.g. `useUser()` falls back to the
    // redux-persisted membership.userId once the Supabase session goes null, which
    // happens right on sign-out, before the page reload that follows it lands. An
    // unguarded query here sent `user_id=eq.undefined` to PostgREST, which Postgres
    // rejects with "invalid input syntax for type uuid", reported to Sentry and then
    // rethrown into the error boundary as an unhandled onboarding crash.
    if (!user.id) { return }

    // attempt reload from server
    const row = await selectMembershipForUser(client, { userId: user.id })

      if (row) {
        const {role, membership, profile, organization} = row

        dispatch(onSyncMembership({
          userId: user?.id,
          membership,
          role,
          profile,
          organization
        }))
      }
  }, [user.id])

  if (init.error) { throw init.error }

  if (init.loading) {
    return <div className="row mt-4">
      <div className="col-12 col-xl-8 offset-xl-2">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    </div>
  }

  if (membershipSlice.uninitialized) {
    return <MembershipInsertForm user={user} onComplete={() => onComplete()} />
  }

  const {membershipId, member_number, sharing_ministry_type, organization, role} = membershipSlice

  // A free-trial invitation pre-creates the membership row (with this user as
  // admin) but never sets a sharing ministry, and such users have no organization
  // to supply one. The member-number-only update form below can't set the ministry
  // type, so these users could never pick Sedera/CHM. Show the full ministry
  // selection form instead — it updates the existing row in place rather than
  // inserting a duplicate.
  if (role === 'admin' && !sharing_ministry_type && !organization) {
    return <MembershipInsertForm user={user} existingMembershipId={membershipId} onComplete={() => onComplete()} />
  }

  if (role === 'admin') {
    return <MembershipUpdateForm user={user} existingMembership={{
      id: membershipId,
      member_number
    }} onUpdate={
      () => onComplete()
    } />
  }

  // not an admin
  return <div className="row">
    <div className="col-12">
      <p>
        Please contact us to help you correct your membership number.
        You will not be able to take advantage of all the features of this app until you set your membership number.
      </p>
      <button className="btn btn-secondary" onClick={onComplete}>
        Come back to this later
      </button>
    </div>
  </div>
}

type MinistrySelection = 'CHM' | 'Sedera' | 'SMI' | 'none' | ''

// Sedera members choose an Initial Unshareable Amount (IUA) at enrollment that
// applies per medical need. These are Sedera's standard tiers.
const SEDERA_IUA_TIERS = [500, 1000, 1500, 2500, 5000] as const

// Samaritan members choose a plan level at enrollment; the plan determines the
// per-need Initial Unshareable Amount (see SMI_PLANS in lib/rules/limits.ts).
const SMI_PLAN_OPTIONS: Array<{ value: SmiPlan, label: string }> = [
  { value: 'classic', label: 'Samaritan Classic — $1,000 IUA per need, 100% shared above it' },
  { value: 'basic', label: 'Samaritan Basic — $2,000 IUA per need, 90% shared above it' },
]

interface MembershipInsertFormProps {
  user: { id: string }

  // When set, the user already has a membership row (e.g. pre-created by a
  // free-trial invitation) that has no sharing ministry yet. We update that row
  // in place rather than inserting a new one, so we don't create a duplicate
  // membership.
  existingMembershipId?: string

  onComplete?: () => void
}

function MembershipInsertForm({
    user,
    existingMembershipId,
    onComplete
}: MembershipInsertFormProps) {
  const [ministrySelection, setMinistrySelection] = useState<MinistrySelection>('')
  const [memberNumber, setMemberNumber] = useState<string>()
  const [iuaAmount, setIuaAmount] = useState<number | ''>('')
  const [smiPlan, setSmiPlan] = useState<SmiPlan | ''>('')
  const api = useBackendApi()
  const dispatch = useAppDispatch()

  const [submitResult, onSubmit] = useAsyncAction(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!(e.target as HTMLFormElement).checkValidity()) { return }

    if (!ministrySelection) { return }

    const sharingMinistryType: SharingMinistryType =
      ministrySelection === 'CHM' ? 'CHM' :
      ministrySelection === 'Sedera' ? 'Sedera' :
      ministrySelection === 'SMI' ? 'SMI' :
      null

    if (sharingMinistryType === 'CHM' && !memberNumber) { return }
    if (sharingMinistryType === 'Sedera' && !iuaAmount) { return }
    if (sharingMinistryType === 'SMI' && (!memberNumber || !smiPlan)) { return }

    // One call, one transaction: the membership, the caller's admin role, and
    // the organization link. This used to be three sequential PostgREST writes
    // with nothing tying them together -- a failure after the first left a
    // membership its own creator could not administer. See
    // docs/plans/native-app-sdk-seams.md, Stage 2 Step 4.
    const saved = await saveMyMembership(api, {
      membershipId: existingMembershipId,
      memberNumber: sharingMinistryType === 'CHM' || sharingMinistryType === 'SMI' ? memberNumber! : null,
      sharingMinistryType,
      iuaAmount: sharingMinistryType === 'Sedera' ? (iuaAmount as number) : null,
      smiPlan: sharingMinistryType === 'SMI' ? (smiPlan as SmiPlan) : null,
    })
    dispatch(onSyncMembership({
      userId: user.id,
      ...saved,
    }))

    onComplete && onComplete()
  }, [user.id, existingMembershipId, memberNumber, iuaAmount, smiPlan, ministrySelection, api, dispatch, onComplete])

  let errorMessage: React.ReactNode | undefined = undefined
  if (submitResult.error) {
    if (submitResult.error.message.includes('duplicate key value violates unique constraint')) {
      errorMessage = <p>
        This membership number belongs to another account.<br/>
        Please <Link to="/help">contact us for help</Link> if you would like to manage this
        membership number using this email address.
      </p>
    } else {
      throw submitResult.error
    }
  }

  return <div className="row">
    <div className="col-12">
      <h1>Enter your Membership Details</h1>
      <p>
        This app helps you track medical expenses and manage reimbursements.
      </p>

      {submitResult.loading &&
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>}

      <form onSubmit={onSubmit}>
        <div className="form-group col-12 col-lg-6">
          <label htmlFor="ministrySelect" className="form-label">Health Care Sharing Ministry</label>
          <select
            className="form-select"
            id="ministrySelect"
            value={ministrySelection}
            disabled={submitResult.loading}
            required
            onChange={(e) => {
              setMinistrySelection(e.target.value as MinistrySelection)
            }}
          >
            <option value="" disabled>Select your sharing ministry...</option>
            <option value="CHM">Christian Healthcare Ministries (CHM)</option>
            <option value="Sedera">Sedera</option>
            <option value="SMI">Samaritan Ministries International</option>
            <option value="none">I don't have a sharing ministry</option>
          </select>

          {ministrySelection === 'CHM' && <>
            <label htmlFor="memberNumber" className="form-label mt-3">CHM Member Number</label>
            <input type="text" className="form-control" id="memberNumber"
              value={memberNumber || ''}
              disabled={submitResult.loading}
              required
              minLength={5}
              onChange={(e) => { setMemberNumber(e.target.value) }} />
          </>}

          {ministrySelection === 'Sedera' && <>
            <label htmlFor="iuaAmount" className="form-label mt-3">Initial Unshareable Amount (IUA)</label>
            <select
              className="form-select"
              id="iuaAmount"
              value={iuaAmount === '' ? '' : String(iuaAmount)}
              disabled={submitResult.loading}
              required
              onChange={(e) => { setIuaAmount(e.target.value === '' ? '' : Number(e.target.value)) }}
            >
              <option value="" disabled>Select your IUA...</option>
              {SEDERA_IUA_TIERS.map((amount) => (
                <option key={amount} value={amount}>${amount.toLocaleString()}</option>
              ))}
            </select>
          </>}

          {ministrySelection === 'SMI' && <>
            <label htmlFor="memberNumber" className="form-label mt-3">Samaritan Member Number</label>
            <input type="text" className="form-control" id="memberNumber"
              value={memberNumber || ''}
              disabled={submitResult.loading}
              required
              minLength={5}
              onChange={(e) => { setMemberNumber(e.target.value) }} />

            <label htmlFor="smiPlan" className="form-label mt-3">Samaritan Plan</label>
            <select
              className="form-select"
              id="smiPlan"
              value={smiPlan}
              disabled={submitResult.loading}
              required
              onChange={(e) => { setSmiPlan(e.target.value as SmiPlan | '') }}
            >
              <option value="" disabled>Select your plan...</option>
              {SMI_PLAN_OPTIONS.map(({ value, label }) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </>}

          {errorMessage && <div className="alert alert-danger mt-2">{errorMessage}</div>}

            <button type='submit'
              className={`btn btn-primary mt-2`}
              disabled={!ministrySelection || submitResult.loading}
            >
              {submitResult.loading ?
                <span className="spinner-border" role="status">
                  <span className="visually-hidden">Submitting...</span>
                </span> :
                `Submit`}
            </button>
        </div>
      </form>
    </div>
  </div>
}

interface MembershipUpdateFormProps {
  user: { id: string }

  existingMembership: Pick<MembershipRow, 'id' | 'member_number'>

  onUpdate?: () => void
}

function MembershipUpdateForm({
  user,
  existingMembership,
  onUpdate
}: MembershipUpdateFormProps) {
  const [memberNumber, setMemberNumber] = useState<string>(existingMembership.member_number || '')
  const api = useBackendApi()
  const dispatch = useAppDispatch()
  // Read ministry-specific state for the read-only tier displays below.
  const sharingMinistryType = useAppSelector((s) => s.membership.sharing_ministry_type)
  const iuaAmount = useAppSelector((s) => s.membership.iua_amount)
  const smiPlan = useAppSelector((s) => s.membership.smi_plan)

  const [submitResult, onSubmit] = useAsyncAction(async (e: React.FormEvent) => {
    e.preventDefault();

    if (sharingMinistryType === 'Sedera') {
      onUpdate && onUpdate()
      return
    }

    if (!memberNumber) { return }

    const membership = await updateMyMembership(api, existingMembership.id, {
      member_number: memberNumber,
    })
    if (!membership.member_number) {
      throw new Error(`Unable to update member number for membership ${existingMembership.id}`)
    }
    dispatch(onUpdateMemberNumber({
      member_number: membership.member_number
    }))

    onUpdate && onUpdate()
  }, [existingMembership.id, memberNumber, sharingMinistryType, api, dispatch, onUpdate])

  return <div className="row">
    <div className="col-12">
      <h1>Enter your Membership Details</h1>
      <p>
        {sharingMinistryType === 'Sedera'
          ? 'Your IUA tier is shown below.'
          : 'Please enter your sharing-ministry membership number.'}
      </p>

      {submitResult.loading &&
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>}

      <form onSubmit={onSubmit}>
        {sharingMinistryType !== 'Sedera' && (
          <div className="form-group">
            <label htmlFor="memberNumber" className="form-label">Member Number</label>
            <input type="text" className="form-control" id="memberNumber"
              value={memberNumber}
              disabled={submitResult.loading}
              onChange={(e) => { setMemberNumber(e.target.value) }} />
          </div>
        )}
        {sharingMinistryType === 'Sedera' && iuaAmount != null && (
          <div className="form-group mt-3">
            <label htmlFor="iuaAmountReadOnly" className="form-label">Initial Unshareable Amount (IUA)</label>
            <input type="text" className="form-control" id="iuaAmountReadOnly"
              value={`$${iuaAmount.toLocaleString()}`}
              disabled
              readOnly />
            <small className="form-text text-muted">
              To change your IUA tier, please <Link to="/help">contact support</Link>.
            </small>
          </div>
        )}
        {sharingMinistryType === 'SMI' && smiPlan != null && (
          <div className="form-group mt-3">
            <label htmlFor="smiPlanReadOnly" className="form-label">Samaritan Plan</label>
            <input type="text" className="form-control" id="smiPlanReadOnly"
              value={smiPlan === 'basic' ? 'Samaritan Basic ($2,000 IUA per need)' : 'Samaritan Classic ($1,000 IUA per need)'}
              disabled
              readOnly />
            <small className="form-text text-muted">
              To change your plan level, please <Link to="/help">contact support</Link>.
            </small>
          </div>
        )}
        <div className="col-12">
          {submitResult.error &&
            <div className="text-danger mt-2">{submitResult.error.message}</div>}
            <button type='submit'
              className={`btn btn-primary ${submitResult.loading ? 'disabled' : ''} mt-2`}
            >
              {submitResult.loading ?
                <span className="spinner-border" role="status">
                  <span className="visually-hidden">Submitting...</span>
                </span> :
                `Submit`}
            </button>
        </div>
      </form>

    </div>
  </div>
}
